import { $generateNodesFromDOM } from '@lexical/html';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { AutoLinkPlugin, createLinkMatcherWithRegExp } from '@lexical/react/LexicalAutoLinkPlugin';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TablePlugin } from '@lexical/react/LexicalTablePlugin';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import classnames from 'classnames';
import { $getRoot } from 'lexical';
import { useEffect, useState } from 'react';

import { ClipboardPlugin } from './ClipboardPlugin';
import { DisablePlugin } from './DisablePlugin';
import { FloatingLinkEditorPlugin } from './FloatingLinkPopoverPlugin';
import { HtmlPlugin } from './HtmlPlugin';
import { PlaceholderPlugin } from './PlaceholderPlugin';
import styles from './RichTextArea.module.scss';
import { RichTextAreaProps } from './RichTextArea.types';
import { TableActionMenuPlugin } from './TableActionMenuPlugin/TableActionMenuPlugin.component';
import { TableHoverActionsPlugin } from './TableHoverActionsPlugin';
import { ToolbarPlugin } from './ToolbarPlugin';

const THEME = {
  link: styles.link,
  paragraph: styles.paragraph,
  text: {
    bold: styles.bold,
    italic: styles.italic,
    underline: styles.underline,
  },
  table: styles.table,
  tableCell: styles.tableCell,
  tableCellHeader: styles.tableCellHeader,
  tableCellSelected: styles.tableCellSelected,
  tableRow: styles.tableRow,
  tableAddRows: styles.tableAddRows,
  tableAddColumns: styles.tableAddColumns,
};

const AUTO_LINK_MATCHERS = [
  createLinkMatcherWithRegExp(
    /((https?:\/\/(www\.)?)|(www\.))[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)(?<![-.+():%])/,
    text => (text.startsWith('http') ? text : `https://${text}`)
  ),
  createLinkMatcherWithRegExp(
    /(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))/,
    text => `mailto:${text}`
  ),
];

export const RichTextArea = ({
  value,
  onChange,
  className,
  autoFocus,
  error,
  disabled,
  placeholder,
  toolbarChildren,
}: RichTextAreaProps) => {
  const [innerHtml, setInnerHtml] = useState(value);
  const [isLinkEditOpen, setIsLinkEditOpen] = useState(false);

  useEffect(() => {
    onChange(innerHtml);
  }, [innerHtml, onChange]);

  return (
    <div className={styles.container}>
      <LexicalComposer
        initialConfig={{
          editorState: editor => {
            const parser = new DOMParser();
            const dom = parser.parseFromString(innerHtml, 'text/html');
            const nodes = $generateNodesFromDOM(editor, dom);
            const root = $getRoot();
            root.append(...nodes);
          },
          namespace: 'lexical',
          nodes: [AutoLinkNode, LinkNode, TableNode, TableCellNode, TableRowNode],
          theme: THEME,
          onError: error => {
            throw error;
          },
          editable: !disabled,
        }}
      >
        <ToolbarPlugin setIsLinkEditOpen={setIsLinkEditOpen} toolbarChildren={toolbarChildren} />
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={classnames(styles.editor, className, {
                [styles.error]: error,
                [styles.disabled]: disabled,
              })}
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <TablePlugin hasCellBackgroundColor={false} />
        {autoFocus && <AutoFocusPlugin />}
        <ClipboardPlugin />
        <PlaceholderPlugin placeholder={placeholder} />
        <DisablePlugin disabled={disabled} />
        <HtmlPlugin value={value} innerHtml={innerHtml} setInnerHtml={setInnerHtml} />
        <FloatingLinkEditorPlugin
          isLinkEditOpen={isLinkEditOpen}
          setIsLinkEditOpen={setIsLinkEditOpen}
        />
        <TableActionMenuPlugin />
        <TableHoverActionsPlugin />
        <LinkPlugin
          validateUrl={url =>
            /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[\w]*))?)/.test(
              url
            )
          }
          attributes={{
            rel: 'noopener noreferrer',
            target: '_blank',
          }}
        />
        <AutoLinkPlugin matchers={AUTO_LINK_MATCHERS} />
      </LexicalComposer>
    </div>
  );
};
